import { ReactNode } from "react";

// Components
export interface IInput {
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  maxChars: number;
  type: "text" | "password";
  hasLabel: boolean;
  label: string;
  required: boolean;
  isPassword: boolean;
  error?: string;
}
export interface IButton {
  children: ReactNode;
  action: () => void;
  className?: string;
  disabled?: boolean;
}
export interface ILinkButton {
  to: string;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
}
export interface ICountryInput {
  country: ICountry;
  setCountry: (country: ICountry) => void;
  required: boolean;
  hasLabel: boolean;
  label: string;
  editable: boolean;
}
export interface ICalendarInput {
  date: Date;
  setDate: (date: Date) => void;
  required: boolean;
  hasLabel: boolean;
  label: string;
  error?: string;
}
export interface IDocumentInput {
  document: IDocument;
  setDocument: (document: IDocument) => void;
  country_id: string;
  required: boolean;
  label: string;
  hasLabel: boolean;
}
export interface IGenderInput {
  gender: string;
  setGender: (gender: string) => void;
  required: boolean;
  hasLabel: boolean;
  label: string;
}
export interface IToast {
  message: string;
  type: "success" | "info" | "warning" | "error";
  showToast: boolean;
  setShowToast: (show: boolean) => void;
}
export interface IEventCard {
  event: IEvent;
}
export interface ICircularLoading {
  size: "sm" | "md" | "lg";
  className?: string;
}
export interface IInscriptionDetailCard {
  inscriptionId: string;
}
export interface INavbar {}

// States
export interface ILoginFormData {
  email: string;
  password: string;
}
export interface IRegisterFormData {
  email: string;
  password: string;
  password_confirmation: string;
  name: string;
  last_name: string;
  gender: "male" | "female";
  country_id: string;
  birthdate: Date;
  phone_number: string;
  emergency_phone_number: string;
  document: IDocumentToServer;
}
export interface IDocument {
  id: string;
  slug: string;
  mask: string;
  value: string;
  name: string;
}
export interface IDocumentToServer {
  document_type_id: string;
  value: string;
}
export interface ICountry {
  id: string;
  name: string;
  iso2_code: string;
  image: string;
}
export interface IToken {
  access_token: string;
  token_type: string;
}
export interface IUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  gender: "male" | "female";
  country: ICountry;
  date_of_birth: Date;
  phone_number: string;
  emergency_phone_number: string;
  created_at: Date;
  document: IDocumentProfile;
  should_update_birthdate: boolean;
}
export interface IDocumentProfile {
  document_type: string;
  id: string;
  value: string;
}
export interface IEventStatus {
  name: string;
  slug: string;
}
export interface IEvent {
  id: string;
  slug: string;
  name: string;
  image_url: string;
  description: string;
  start_date: string;
  registration_start_date: string;
  registration_end_date: string;
  range_prices: string;
  active: boolean;
  status: IEventStatus;
}
export interface IEventDetail {
  id: string;
  slug: string;
  name: string;
  image_url: string;
  description: string;
  location: ILocation;
  start_date: string;
  registration_start_date: string;
  registration_end_date: string;
  kits_included: IKitItem[];
  active: boolean;
  without_shirts: boolean;
  status: IEventStatus;
}
export interface IKitItem {
  key: string;
  value: string;
}
export interface ILocation {
  latitude: number;
  longitude: number;
}
export interface IKm {
  id: string;
  name: string;
  slug: string;
  distance: string;
  price: number;
  description: string | null;
  start_date: string;
  garmin_course_id: string | null;
}
export interface IShirt {
  id: string;
  name: string;
  image: string;
  /** Model (Camiseta, Centro…). Older API builds omit it; the name is parsed instead. */
  type?: { slug: string; name: string } | null;
  size?: string | null;
}
export interface ITotalCart {
  shirt_selected: IShirtSelected;
  distance_event_selected: IDistanceSelected;
  total: number;
  already_inscribed: boolean | undefined;
  fee: number;
  discount?: number;
  /** Present when a coupon_code was sent; null otherwise. */
  coupon?: ICouponQuote | null;
}
export interface ICouponQuote {
  code: string;
  applied: boolean;
  /** applied | not_found | not_for_event | not_started | expired | exhausted | already_used | no_amount | not_best */
  status: string;
  message: string;
}
export interface IShirtSelected {
  id: string;
  name: string;
  price: number;
}
export interface IDistanceSelected {
  id: string;
  name: string;
  distance: string;
  price: number;
}
export interface ITotalPriceArgs {
  event_id: string;
  shirt_id?: string | null;
  distance_event_id: string;
  access_token?: string | null;
  coupon_code?: string | null;
}
export interface ICompletePaymentArgs {
  event_id: string;
  shirt_id: string | null;
  distance_event_id: string;
  access_token: string;
  coupon_code?: string | null;
}
/** POST /athlete/events/{uuid}/inscriptions. A $0 total (coupon covers it all) skips the gateway: no link, already inscribed. */
export interface ICheckoutResult {
  link_payment: string | null;
  inscription_id?: string;
}
export interface IInscriptionDetailArgs {
  access_token: string;
  inscriptionId: string;
}
/** GET /athlete/inscriptions/{uuid} (DetailInscriptionResource). */
export interface IInscriptionDetail {
  id: string;
  code: string | null;
  inscription_status: { uuid?: string; name: string; slug: string };
  distance_event: {
    id: string;
    name: string;
    distance: string;
    start_date: string;
    event: {
      id: string;
      name: string;
      image_url: string;
      location: ILocation | null;
      start_date: string;
    };
  };
  shirt: { id: string; name: string; image: string; shirt_size: { name: string } | null } | null;
}
export interface IResetPassData {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

// Functions
export const getMonthFromDate = (dateString: string): string => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(dateString);

  const abbreviatedMonth = months[date.getUTCMonth()];

  return abbreviatedMonth;
};

export const getDayFromDate = (dateString: string): number => {
  const date = new Date(dateString);

  const day = date.getUTCDate();

  return day;
};

export const getFriendlyDate = (dateString: string): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Fecha inválida";
  }

  const formatter = new Intl.DateTimeFormat("es-SV", {
    timeZone: "America/El_Salvador",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  return formatter.format(date);
};

export const convertDateString = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const dateObject = new Date(dateString);

  if (isNaN(dateObject.getTime())) {
    return "Fecha inválida";
  }

  dateObject.setUTCHours(
    dateObject.getUTCHours() + dateObject.getTimezoneOffset() / 60
  );

  return dateObject.toLocaleDateString("es-ES", options);
};

export const convertTimeString = (timeString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: "America/El_Salvador",
  };

  const timeObject = new Date(timeString);

  if (isNaN(timeObject.getTime())) {
    return "Hora inválida";
  }

  return timeObject.toLocaleTimeString("en-US", options);
};

export const convertDateObjectToString = (dateObject: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  if (isNaN(dateObject.getTime())) {
    return "Fecha inválida";
  }

  return dateObject.toLocaleDateString("es-ES", options);
};

export const validateIsEmail = (email: string): boolean => {
  const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

export const formatDateForEventCard = (dateString: string): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Fecha inválida";
  }

  const formatter = new Intl.DateTimeFormat("es-SV", {
    timeZone: "America/El_Salvador",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return formatter.format(date);
};

export const formatTimeWithTimezone = (
  dateString: string
): { time: string; timezone: string } => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return { time: "Hora inválida", timezone: "" };
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/El_Salvador",
  };

  const time = date.toLocaleTimeString("es-SV", options);

  return {
    time,
    timezone: "(CST - El Salvador)",
  };
};
